<template>
  <button
    v-if="clickable"
    type="button"
    class="member-avatar"
    :class="{ 'member-avatar--button': true }"
    :style="sizeStyle"
    :aria-label="ariaLabel"
    @click.stop="$emit('open')"
  >
    <img v-if="avatarUrl" :src="avatarUrl" :alt="ariaLabel" class="member-avatar__img" />
    <span v-else class="member-avatar__initials" :style="bgStyle">{{ initials }}</span>
  </button>
  <span
    v-else
    class="member-avatar"
    :style="sizeStyle"
    :aria-label="ariaLabel"
  >
    <img v-if="avatarUrl" :src="avatarUrl" :alt="ariaLabel" class="member-avatar__img" />
    <span v-else class="member-avatar__initials" :style="bgStyle">{{ initials }}</span>
  </span>
</template>

<script>
import { initialsFor } from '@/utils/community';

// Deterministic muted background per name — same name always gets the
// same color so members can recognize each other by tint even with
// initials-only fallback. Palette is intentionally desaturated: slate,
// taupe, deep navy, deep plum, deep teal. No saturated brand colors.
const PALETTE = [
  '#4A5566', // slate
  '#6E5F50', // taupe
  '#2D3A5C', // deep navy
  '#5C3A50', // deep plum
  '#2D5C5A', // deep teal
];
function hashColor(seed) {
  let h = 0;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

export default {
  name: 'MemberAvatar',
  props: {
    avatarUrl: { type: String, default: '' },
    name: { type: String, default: '' },
    size: { type: Number, default: 40 },
    clickable: { type: Boolean, default: false },
  },
  emits: ['open'],
  computed: {
    initials() { return initialsFor(this.name); },
    sizeStyle() {
      return { width: `${this.size}px`, height: `${this.size}px` };
    },
    bgStyle() { return { background: hashColor(this.name) }; },
    ariaLabel() { return this.name ? `${this.name} 프로필 보기` : '프로필 보기'; },
  },
};
</script>

<style scoped>
.member-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--surface-3);
  border: 0;
  padding: 0;
}
.member-avatar--button {
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
}
.member-avatar--button:active { transform: scale(0.94); }

.member-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.member-avatar__initials {
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* The tint behind these initials is a RUNTIME :style binding (a dark muted
     palette in this component's script), so no static tool can see it. The
     contrast codemod assumed the page ground and rewrote white → ink, which
     put dark-on-dark initials on every avatar in 소통방. White is correct here
     and must stay: every colour in the palette is dark by construction. */
  color: #FFFFFF;
  font: inherit;
  /* Initials should feel like they fill the circle — sized as a percentage
     of the avatar diameter via font-size. ~58% reads as confidently
     occupying the circle at every size (32px strip, 88px hero, 160px setup)
     without crowding the edge. Weight bumped to 600 so the letterforms
     have presence at large sizes; line-height: 1 keeps them vertically
     centered without descender drift. */
  font-weight: 600;
  font-size: 58%;
  line-height: 1;
  letter-spacing: -0.02em;
}
</style>
